import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, interval, EMPTY } from 'rxjs';
import { catchError, switchMap, startWith } from 'rxjs/operators';
import { resolveUrl, API_PREFIX } from 'src/core/constants/api.constants';
import { ImportState, ImportRequest, ImportSource } from '../models';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { LocalizationService } from '@abp/ng.core';

const DATA_IMPORT = `${API_PREFIX}/data-import`;

/**
 * Service for managing data import operations
 * Handles import status polling, starting imports, and retrieving system data
 */
@Injectable({
  providedIn: 'root',
})
export class DataImportService {
  constructor(
    private readonly http: HttpClient,
    private readonly toastr: ToasterService,
    private readonly localization: LocalizationService
  ) {}

  /**
   * Initiates a data import operation
   * @param request - Import request containing source and scope configuration
   */
  startImport(request: ImportRequest): Observable<void> {
    return this.http
      .post<void>(resolveUrl(`${DATA_IMPORT}/start-import`), request)
      .pipe(catchError(error => this.handleError('startImport', error)));
  }

  /**
   * Retrieves the current import status for a specific source
   * @param source - Import source (CA or VA)
   */
  getImportStatus(source: ImportSource): Observable<ImportState> {
    const params = new HttpParams().set('source', source);
    return this.http
      .get<ImportState>(resolveUrl(`${DATA_IMPORT}/import-status`), { params })
      .pipe(catchError(error => this.handleError('getImportStatus', error)));
  }

  /**
   * Retrieves the system start date for a specific source
   * @param source - Import source (CA or VA)
   */
  getSystemStartDate(source: ImportSource): Observable<Date> {
    const params = new HttpParams().set('source', source);
    return this.http
      .get<Date>(resolveUrl(`${DATA_IMPORT}/system-start-date`), { params })
      .pipe(catchError(error => this.handleError('getSystemStartDate', error)));
  }

  /**
   * Stops the active import operation for the specified source
   * @param source - Import source (CA or VA)
   */
  stopImport(source: ImportSource): Observable<void> {
    const params = new HttpParams().set('source', source);
    return this.http
      .post<void>(resolveUrl(`${DATA_IMPORT}/stop-import`), null, { params })
      .pipe(catchError(error => this.handleError('stopImport', error)));
  }

  /**
   * Polls the import status at regular intervals
   * @param source - Import source (CA or VA)
   * @param intervalMs - Polling interval in milliseconds (default: 2000)
   */
  pollImportStatus(source: ImportSource, intervalMs = 2000): Observable<ImportState> {
    return interval(intervalMs).pipe(
      startWith(0),
      switchMap(() => this.getImportStatus(source))
    );
  }

  private handleError(context: string, error: unknown): Observable<never> {
    const abpError = (error as any)?.error?.error;
    const errorCode: string | undefined = abpError?.code;

    if (errorCode) {
      const localizedMessage = this.localization.instant(errorCode);
      const warningTitle = this.localization.instant('::Warning');
      this.toastr.error(localizedMessage, warningTitle);
      return EMPTY;
    }

    return throwError(() => error);
  }
}
